/**
 * Phase 107 (Plan 107-08 / D-04) — "+ Add band" form modal.
 *
 * There is no standalone modal file to recover: the deleted equivalent was
 * a private inline function at the bottom of
 * `frontend/src/pages/projects/CoveragePage.tsx` (git show bec322bc, around
 * line 483), next to the color-palette constant (around line 30). This file
 * ports that form's fields and validation but wraps them in the shared
 * `common/Modal.tsx` shell rather than the recovered inline fixed-overlay
 * div — every sibling modal in this phase uses the shared shell.
 *
 * The recovered abbreviation bound was 2-3 characters; 107-UI-SPEC.md
 * deliberately widens it to 2-4.
 */
import { useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';

/** Ported verbatim from the deleted `TEAM_COLOR_PALETTE` in `CoveragePage.tsx`. */
export const BAND_COLOR_PALETTE = [
  '#D2691E',
  '#10B981',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#F59E0B',
] as const;

const DEFAULT_BAND_COLOR = BAND_COLOR_PALETTE[0];

// Ported verbatim from `CellRateEditorPopover.tsx`'s `normalizeDecimal` +
// `validate` — see `BandPanel.tsx` for the full commentary on why this must
// not be re-derived from scratch.
function normalizeDecimal(input: string): string {
  return input.replace(',', '.');
}

function validateRate(input: string): string | null {
  if (input.trim() === '') return null;
  const normalized = normalizeDecimal(input);
  if (!/^[0-9]+(\.[0-9]{0,2})?$/.test(normalized)) {
    if (/\.[0-9]{3,}/.test(normalized)) return 'Rate supports up to 2 decimal places.';
    return 'Enter a numeric rate (e.g. 120 or 120.50).';
  }
  const n = Number(normalized);
  if (!Number.isFinite(n)) return 'Enter a numeric rate (e.g. 120 or 120.50).';
  if (n < 0) return 'Rate must be 0 or greater.';
  if (n > 99999.99) return 'Rate must be 99999.99 or less.';
  return null;
}

export interface AddBandModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: { name: string; abbreviation: string; color: string; hourlyRate: string | null }) => void;
  /** Used to cycle the default palette suggestion by band count. */
  existingBandCount: number;
  isSubmitting?: boolean;
}

export function AddBandModal({
  isOpen,
  onClose,
  onSubmit,
  existingBandCount,
  isSubmitting = false,
}: AddBandModalProps) {
  const [name, setName] = useState('');
  const [abbreviation, setAbbreviation] = useState('');
  const [color, setColor] = useState<string>(
    BAND_COLOR_PALETTE[existingBandCount % BAND_COLOR_PALETTE.length] ?? DEFAULT_BAND_COLOR,
  );
  const [rate, setRate] = useState('');

  const rateError = validateRate(rate);
  const isValid =
    name.trim().length >= 1 &&
    abbreviation.trim().length >= 2 &&
    abbreviation.trim().length <= 4 &&
    /^#[0-9A-Fa-f]{6}$/.test(color) &&
    rateError === null;

  function reset() {
    setName('');
    setAbbreviation('');
    setColor(BAND_COLOR_PALETTE[existingBandCount % BAND_COLOR_PALETTE.length] ?? DEFAULT_BAND_COLOR);
    setRate('');
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    const trimmedRate = rate.trim();
    onSubmit({
      name: name.trim(),
      abbreviation: abbreviation.trim().toUpperCase(),
      color,
      hourlyRate: trimmedRate === '' ? null : normalizeDecimal(trimmedRate),
    });
    reset();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add band"
      ariaLabelledBy="add-band-modal-title"
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid} isLoading={isSubmitting}>
            + Add band
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit}>
        <label className="mb-3 block text-[12px]">
          <span className="block font-medium text-gray-700">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-[13px]"
            placeholder="Night hours"
            autoFocus
          />
        </label>
        <label className="mb-3 block text-[12px]">
          <span className="block font-medium text-gray-700">Abbreviation (2-4 chars)</span>
          <input
            type="text"
            value={abbreviation}
            onChange={(e) => setAbbreviation(e.target.value)}
            maxLength={4}
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-[13px] uppercase"
            placeholder="NGHT"
          />
        </label>
        <label className="mb-3 block text-[12px]">
          <span className="block font-medium text-gray-700">Color</span>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {BAND_COLOR_PALETTE.map((swatch) => (
              <button
                key={swatch}
                type="button"
                onClick={() => setColor(swatch)}
                aria-label={`Use color ${swatch}`}
                aria-pressed={color === swatch}
                className={`h-6 w-6 rounded ${color === swatch ? 'ring-2 ring-offset-1 ring-gray-500' : ''}`}
                style={{ backgroundColor: swatch }}
              />
            ))}
            <input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="flex-1 rounded border border-gray-300 px-2 py-1 font-mono text-[13px]"
              placeholder="#D2691E"
              aria-label="Custom hex color"
            />
          </div>
        </label>
        <label className="mb-1 block text-[12px]">
          <span className="block font-medium text-gray-700">Hourly rate (optional)</span>
          <input
            type="text"
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="0.00"
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-[13px]"
          />
        </label>
        {rateError && <p className="text-[11px] text-rose-600">{rateError}</p>}
      </form>
    </Modal>
  );
}

export default AddBandModal;
